import {
  TeamPerformanceInsightsRangePreset,
  ProgramTagDataFragment,
} from '../../generated/graphql';
import SelectMenu from './SelectMenu';
import MultiPicker from './MultiPicker';
import { InsightsFilterState } from '../pages/Insights/insightsFilters';

const PERIOD_OPTIONS = [
  {
    label: 'This week',
    value: TeamPerformanceInsightsRangePreset.CalendarWeek,
  },
  {
    label: 'This month',
    value: TeamPerformanceInsightsRangePreset.CalendarMonth,
  },
  {
    label: 'This year',
    value: TeamPerformanceInsightsRangePreset.CalendarYear,
  },
  { label: 'Custom range', value: TeamPerformanceInsightsRangePreset.Custom },
  { label: 'All time', value: TeamPerformanceInsightsRangePreset.AllTime },
];

type InsightsFilterBarProps = {
  value: InsightsFilterState;
  onChange: (next: InsightsFilterState) => void;
  tags: ProgramTagDataFragment[];
  services: { id: string; name: string }[];
};

// "From"/"To" sit inside the field, so choosing "Custom range" never changes the row's height.
const dateFieldClassName =
  'flex items-center gap-x-3 rounded-md border border-neutral-100 bg-white px-3 py-[15px] text-caption text-neutral-150 shadow-sm focus-within:ring-2 focus-within:ring-secondary-50';

const dateInputClassName =
  'border-0 bg-transparent p-0 text-caption text-secondary-150 focus:outline-none focus:ring-0';

const InsightsFilterBar = ({
  value,
  onChange,
  tags,
  services,
}: InsightsFilterBarProps) => {
  const update = (patch: Partial<InsightsFilterState>) =>
    onChange({ ...value, ...patch });

  const tagOptions = tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
    color: tag.color,
  }));

  return (
    <div className="mb-8 flex w-full flex-col gap-y-5">
      <div className="flex flex-row flex-wrap items-center gap-4">
        <SelectMenu
          label="Period"
          hideLabel={true}
          fieldValue={value.preset}
          onChange={(preset: TeamPerformanceInsightsRangePreset) =>
            update({ preset })
          }
          fieldOptions={PERIOD_OPTIONS}
          containerClassName="w-44"
          buttonClassName="border border-neutral-100 !bg-white !py-[15px]"
        />
        {value.preset === TeamPerformanceInsightsRangePreset.Custom && (
          <>
            <label className={dateFieldClassName}>
              From
              <input
                type="date"
                className={dateInputClassName}
                value={value.startDate ?? ''}
                max={value.endDate ?? undefined}
                onChange={(event) =>
                  update({ startDate: event.target.value || null })
                }
              />
            </label>
            <label className={dateFieldClassName}>
              To
              <input
                type="date"
                className={dateInputClassName}
                value={value.endDate ?? ''}
                min={value.startDate ?? undefined}
                onChange={(event) =>
                  update({ endDate: event.target.value || null })
                }
              />
            </label>
          </>
        )}
      </div>
      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
        <MultiPicker
          label="Services"
          options={services}
          selectedIds={value.providerServiceIds}
          onChange={(providerServiceIds) => update({ providerServiceIds })}
          placeholder="All services"
        />
        <MultiPicker
          label="Include clients tagged with"
          options={tagOptions.filter(
            (tag) => !value.excludeTagIds.includes(tag.id),
          )}
          selectedIds={value.includeTagIds}
          onChange={(includeTagIds) => update({ includeTagIds })}
          placeholder="Any tag"
        />
        <MultiPicker
          label="Hide clients tagged with"
          options={tagOptions.filter(
            (tag) => !value.includeTagIds.includes(tag.id),
          )}
          selectedIds={value.excludeTagIds}
          onChange={(excludeTagIds) => update({ excludeTagIds })}
          placeholder="None"
        />
      </div>
      <div className="font-sans text-caption text-neutral-110">
        Tag filters apply to both metrics and the client lists below.
      </div>
    </div>
  );
};

export default InsightsFilterBar;
